import axios from 'axios';

// Buffers admin presence/page events client-side and flushes them to the
// backend in batches. Never sends one request per event.
// Phase 1: page_view + heartbeat. Phase 2 (KeyData) adds interaction events.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TRACK_URL = `${BACKEND_URL}/api/admin/page-activity/track`;

const HEARTBEAT_MS = 60 * 1000; // emit one heartbeat per minute
const FLUSH_MS = 20 * 1000;     // ship buffered events every 20s
const MAX_BUFFER = 50;          // also flush early if the buffer grows past this

let queue = [];
let currentPage = null;
let interactedSinceLastBeat = false;
let heartbeatTimer = null;
let flushTimer = null;
let started = false;

const now = () => new Date().toISOString();

const enqueue = (event) => {
  queue.push({ occurredAt: now(), ...event });
  if (queue.length >= MAX_BUFFER) flush(false);
};

const markInteraction = () => {
  interactedSinceLastBeat = true;
};

const flush = (onPageLeave) => {
  if (queue.length === 0) return;
  const events = queue;
  queue = [];

  // Leaving the page: a keepalive fetch survives the tab hiding/closing like
  // sendBeacon did, but unlike a beacon it can carry the X-App header. Without
  // that header the backend reads the customer cookie instead of admin_token and
  // its CSRF guard rejects the request (403 "Missing X-App header").
  if (onPageLeave) {
    fetch(TRACK_URL, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-App': 'admin' },
      body: JSON.stringify({ events })
    }).catch(() => {
      queue = events.concat(queue); // network failure — retry on the next flush
    });
    return;
  }

  axios.post(TRACK_URL, { events }).catch(() => {
    // Tracking is best-effort — drop on failure so the buffer can't grow unbounded.
  });
};

const onHeartbeat = () => {
  if (!currentPage) return;
  enqueue({ eventType: 'heartbeat', page: currentPage, active: interactedSinceLastBeat });
  interactedSinceLastBeat = false;
};

const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') flush(true);
};

const onPageHide = () => flush(true);

const INTERACTION_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

const activityTracker = {
  start() {
    if (started) return;
    started = true;
    INTERACTION_EVENTS.forEach((e) =>
      window.addEventListener(e, markInteraction, { passive: true })
    );
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    heartbeatTimer = setInterval(onHeartbeat, HEARTBEAT_MS);
    flushTimer = setInterval(() => flush(false), FLUSH_MS);
  },

  stop() {
    if (!started) return;
    started = false;
    INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, markInteraction));
    window.removeEventListener('pagehide', onPageHide);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    clearInterval(heartbeatTimer);
    clearInterval(flushTimer);
    flush(true);
    currentPage = null;
    interactedSinceLastBeat = false;
  },

  trackPageView(page) {
    currentPage = page;
    interactedSinceLastBeat = true; // navigating is itself activity
    enqueue({ eventType: 'page_view', page });
  },

  // Phase 2 — specific KeyData actions (copy, generate_link, open_modal, ...)
  trackInteraction(action, metadata = null) {
    enqueue({ eventType: 'interaction', page: currentPage, action, metadata });
  }
};

export default activityTracker;
